import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ATMBreadCrumbs, {
  BreadcrumbType,
} from "src/components/UI/atoms/ATMBreadCrumbs/ATMBreadCrumbs";
import { getTabs } from "src/utils/auth/getTabs";
import AuthHOC from "src/userAccess/AuthHOC";
import { ValidApplicationStatus } from "src/utils/ValidApplicationStatus";
import { twMerge } from "tailwind-merge";

type Props = {
  statusWiseCount: any;
  isCountLoading: boolean;
};

const PANListing = ({ statusWiseCount, isCountLoading = false }: Props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const breadcrumbs: BreadcrumbType[] = [
    {
      label: "Dashboard",
      path: "/dashboard",
    },
    {
      label: "PAN",
    },
  ];

  const applicationStatus = [
    {
      label: "PENDING",
      extraClasses: " text-status-pending ",
      onActiveClassNames: {
        button: "bg-status-pending",
        badge: "text-status-pending",
      },
      path: "PENDING",
      type: "ACTION",
      moduleName: "PAN_APPLICATIONS",
      accessAction: "SHOW_PENDING_APPLICATION",
    },
    {
      label: "IN PROGRESS",
      extraClasses: " text-status-progress",
      onActiveClassNames: {
        button: "bg-status-progress",
        badge: "text-status-progress",
      },
      path: "IN_PROGRESS",
      type: "ACTION",
      moduleName: "PAN_APPLICATIONS",
      accessAction: "SHOW_IN_PROGRESS_APPLICATION",
    },
    {
      label: "FORM FILL PENDING",
      extraClasses: " text-status-formfillpending",
      onActiveClassNames: {
        button: "bg-status-formfillpending",
        badge: "text-status-formfillpending",
      },
      path: "FORM_FILL_PENDING",
      type: "ACTION",
      moduleName: "PAN_APPLICATIONS",
      accessAction: "SHOW_FORM_FILL_PENDING_APPLICATION",
    },
    {
      label: "FORM FILL IN PROGRESS",
      extraClasses: " text-status-formfillprogress",
      onActiveClassNames: {
        button: "bg-status-formfillprogress",
        badge: "text-status-formfillprogress",
      },
      path: "FORM_FILL_IN_PROGRESS",
      type: "ACTION",
      moduleName: "PAN_APPLICATIONS",
      accessAction: "SHOW_FORM_FILL_IN_PROGRESS_APPLICATION",
    },
    {
      label: "LOGIN DONE",
      extraClasses: " text-status-verify",
      onActiveClassNames: {
        button: "bg-status-verify",
        badge: "text-status-verify",
      },
      path: "VERIFY",
      type: "ACTION",
      moduleName: "PAN_APPLICATIONS",
      accessAction: "SHOW_VERIFIED_APPLICATION",
    },
    {
      label: "GENERATE",
      extraClasses: " text-status-generate",
      onActiveClassNames: {
        button: "bg-status-generate",
        badge: "text-status-generate",
      },
      path: "GENERATE",
      type: "ACTION",
      moduleName: "PAN_APPLICATIONS",
      accessAction: "SHOW_GENERATED_APPLICATION",
    },
    {
      label: "DONE",
      extraClasses: " text-status-done",
      onActiveClassNames: {
        button: "bg-status-done",
        badge: "text-status-done",
      },
      path: "DONE",
      type: "ACTION",
      moduleName: "PAN_APPLICATIONS",
      accessAction: "SHOW_DONE_APPLICATION",
    },
  
  ];

  // A tab needs the role's SHOW_* action AND the admin's own Application
  // Status Access tick for that status (Manage Admins > edit admin), so
  // unticking e.g. Done for one admin hides it for that admin only.
  const tabs = getTabs(applicationStatus).filter((status) =>
    AuthHOC({
      forApplicationStatus: true,
      applicationName: "PAN",
      applicationStatus: status.path as ValidApplicationStatus,
      resultType: "BOOLEAN",
    })
  );
  const currentStatus = pathname.split("/")[2];
  const isHiddenTab =
    applicationStatus.some((status) => status.path === currentStatus) &&
    !tabs.some((status) => status.path === currentStatus);

  // Opened a hidden tab by URL: go to the first tab this admin has.
  React.useEffect(() => {
    if (isHiddenTab && tabs.length) navigate(tabs[0].path, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHiddenTab]);

  return (
    <div className="flex flex-col gap-2 px-4 py-3 h-full w-full ">
      <ATMBreadCrumbs breadcrumbs={breadcrumbs} />

      {/* Page Header */}
      <div className="flex 2xl:justify-end xl:justify-end lg:justify-end   xs:justify-normal  items-center gap-2 overflow-x-scroll">
        {tabs?.map((status, statusIndex) => {
          return (
            <button
              key={statusIndex}
              onClick={() => navigate(status.path)}
              className={twMerge(
                ` flex gap-2 items-center font-medium rounded px-4 py-2 text-[13px] duration-300 transition-all relative ${
                  status.extraClasses
                } ${
                  pathname.split("/")[2] === status.path
                    ? `${status.onActiveClassNames.button} text-white `
                    : "bg-slate-50"
                } `
              )}
            >
              {status.label}
              {/* Badge */}
              {isCountLoading ? (
                <div className="h-5 w-5 rounded-full bg-white"></div>
              ) : (
                <div
                  className={twMerge(
                    `  text-[12px] flex justify-center items-center rounded-full font-medium ${status?.label ==='DONE' ? 'w-14 h-6' : ' w-6 h-5'  } ${
                      pathname.split("/")[2] === status.path
                        ? `${status.onActiveClassNames.badge}  bg-white `
                        : "bg-primary-main text-white"
                    } `
                  )}
                >
                  {statusWiseCount && statusWiseCount[status.path]}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {!isHiddenTab && <Outlet />}
    </div>
  );
};

export default PANListing;
